
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";

export const useSignatureGeneration = (
  user: User | null, 
  onSuccess?: (svg: string) => void,
  deductCredits?: () => Promise<boolean>
) => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<string>("");
  const { toast } = useToast();

  const generateSignature = async (text: string, style: string) => {
    if (!text) {
      toast({
        title: "Error",
        description: "Please enter text",
        variant: "destructive",
      });
      return;
    }

    if (!user) {
      toast({
        title: "Error",
        description: "Please log in to generate signatures",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    setStatus("Creating signature...");

    try {
      console.log("[useSignatureGeneration] Starting signature generation for:", { text, style, userId: user.id });
      
      // First deduct credits before generating
      if (deductCredits) {
        const creditDeducted = await deductCredits();
        if (!creditDeducted) {
          toast({
            title: "Error",
            description: "No credits available. Get more credits to generate signatures.",
            variant: "destructive",
          });
          return;
        }
      }

      const { data, error } = await supabase.functions.invoke("replicate", {
        body: { 
          prompt: `Create a clean, text-only signature that shows only "${text}" in a regular weight handwritten calligraphic style. The signature should be simple and elegant, using only regular font weight without any bold or light variations. The signature should be a pure text element without any decorative elements or portraits.`,
          negative_prompt: "NO bold text, NO light text, NO varying weights, NO portraits, NO face silhouettes, NO human figures, NO illustrations, NO decorative elements, NO backgrounds, NO symbols, NO logos, NO frames, NO borders, and absolutely NO visual elements besides the signature text itself. No additional text, no watermarks, no decorative flourishes. The output must be ONLY the handwritten signature text in regular weight.",
          style
        },
      });

      if (error) {
        console.error("[useSignatureGeneration] Error generating signature:", error);
        throw error;
      }
      
      if (!data.output) {
        throw new Error("No output received from signature generation");
      }

      let svgContent;
      if (typeof data.output === 'string' && data.output.startsWith('http')) {
        console.log("[useSignatureGeneration] Fetching SVG from URL:", data.output);
        const response = await fetch(data.output);
        svgContent = await response.text();
      } else {
        svgContent = data.output;
      }

      console.log("[useSignatureGeneration] Successfully generated signature");

      // Immediately call onSuccess to update UI
      if (onSuccess) {
        await onSuccess(svgContent);
      }

      setStatus("Signature created successfully!");
      
      toast({
        title: "Success",
        description: "Signature created successfully!",
      });

      return svgContent;
    } catch (error) {
      console.error("[useSignatureGeneration] Error:", error);
      setStatus("An error occurred during generation");
      toast({
        title: "Error",
        description: "Failed to create signature. Please try again.",
        variant: "destructive",
      });
      return null;
    } finally {
      setLoading(false);
      setStatus("");
    }
  };

  return {
    generateSignature,
    loading,
    status
  };
};

